import { AgGridModule } from "@ag-grid-community/angular";
import { ColDef, GridOptions, ICellRendererParams } from "@ag-grid-community/core";

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { StatsCampaignType } from "@front/m19-api-client";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  MetricCategory,
  ROAS,
  TACOS,
  TARGET_TACOS,
  TOTAL_ORDERS,
  TOTAL_SALES,
} from "@front/m19-metrics";
import { AdStatsEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AdsStatsWithPreviousPeriod,
  adStatsGroupBy,
  AdStatsWithTargetHistory,
  AuthService,
  CurrencyService,
  DateSelection,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  StrategyTargetHistory,
  TacosStrategiesService,
  TacosStrategy,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  ChartConfig,
  IChartComponent,
  IToggleComponent,
  ModalService,
} from "@front/m19-ui";
import { DateAggregation, mergeAdStatsWithTargetHistory, sumAdStats } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ActivityEventFiltersComponent } from "@m19-board/activity-event-filters.component";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { SPONSORED_SALES_SHARE } from "@m19-board/models/MetricsDef";
import { CampaignTypeBadgeGridComponent } from "@m19-board/shared/campaign-type-badge/campaign-type-badge-grid.component";
import { SbCreativeStatsGridComponent } from "@m19-board/strategies/sb-creative-stats-grid/sb-creative-stats-grid.component";
import { combineLatest, map, of, switchMap } from "rxjs";
import { ActivityEventAnnotationsService } from "../../../activities/activity-event-annotations.service";

@Component({
  selector: "app-tacos-stats",
  imports: [
    MetricSelectorComponent,
    IChartComponent,
    AgGridModule,
    ActivityEventFiltersComponent,
    IToggleComponent,
    TranslocoDirective,
    SbCreativeStatsGridComponent,
  ],
  templateUrl: "./tacos-stats.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacosStatsComponent {
  private readonly accountService = inject(AccountSelectionService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly translocoService = inject(TranslocoService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private readonly statsService = inject(StatsService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly currencyService = inject(CurrencyService);
  private readonly modalService = inject(ModalService);
  private destroyRef = inject(DestroyRef);

  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.tacosStats };
  readonly METRICS = [
    TOTAL_SALES,
    TOTAL_ORDERS,
    TACOS,
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CPC,
    CONVERSION_RATE,
    ROAS,
    SPONSORED_SALES_SHARE,
  ];

  tacosStrategy = input.required<TacosStrategy>();
  creativeStatsDetailsModal = viewChild<TemplateRef<unknown>>("creativeStatsDetailsModal");

  currency = toSignal(this.userSelectionService.selectedCurrency$);
  locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)));
  strategyStats = toSignal<AdsStatsWithPreviousPeriod | undefined>(
    combineLatest([
      this.accountService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
      toObservable(this.tacosStrategy),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(([am, dr, periodComparison, currency, tacosStrategy]) =>
        this.tacosStrategiesService.getStats(
          am,
          dr[0],
          dr[1],
          currency,
          periodComparison?.period,
          tacosStrategy?.tacosStrategyGroupId,
        ),
      ),
    ),
  );

  displayEventAnnotation = signal(false);
  allEventAnnotationTypes = this.activityEventAnnotationsService.allActivityEventTypesOptions;

  strategyEventAnnotation = toSignal(
    combineLatest([toObservable(this.tacosStrategy), toObservable(this.displayEventAnnotation)]).pipe(
      switchMap(([tacosStrategy, d]) =>
        d && tacosStrategy
          ? this.activityEventAnnotationsService.getStrategiesActivityEventAnnotation([], undefined, [tacosStrategy])
          : of([]),
      ),
      takeUntilDestroyed(this.destroyRef),
    ),
  );

  readonly totalStats = computed<AdStatsEx>(() => {
    if (!this.strategyStats()) return {};
    return this.strategyStats()!.data.reduce((acc, curr) => {
      sumAdStats(acc, curr);
      return acc;
    }, {});
  });

  readonly previousTotalStats = computed<AdStatsEx>(() => {
    if (!this.strategyStats()) return {};
    return this.strategyStats()!.previousPeriodData.reduce((acc, curr) => {
      sumAdStats(acc, curr);
      return acc;
    }, {});
  });

  rowStats = computed(() => {
    if (!this.strategyStats()) return [];
    return Array.from(this.getStatsByCampaignType(this.strategyStats()!.data).values());
  });

  previousPeriodRowStats = computed<Map<StatsCampaignType | undefined, AdStatsEx>>(() => {
    if (!this.strategyStats()) return new Map();
    return this.getStatsByCampaignType(this.strategyStats()!.previousPeriodData);
  });

  periodComparison = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });
  selectedMetrics = signal<Metric<AdStatsWithTargetHistory>[]>([TOTAL_SALES, AD_SALES]);
  selectedDateRange = toSignal<DateSelection>(this.userSelectionService.selectedDateRange$, { requireSync: true });

  private strategyTargetHistory = toSignal<StrategyTargetHistory[] | undefined>(
    combineLatest([
      this.accountService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
      toObservable(this.tacosStrategy),
    ]).pipe(
      switchMap(([am, dr, currency, _t]) =>
        this.statsService.getStrategyConfigHistory(
          am.accountId,
          am.marketplace,
          currency,
          am.dspAdvertiserId,
          dr[0],
          dr[1],
        ),
      ),
      map((targetHistory) =>
        targetHistory.filter((t) => t.strategyId == this.tacosStrategy()!.spStrategy!.strategyId!),
      ),
      takeUntilDestroyed(this.destroyRef),
    ),
  );

  readonly chartData = computed<AdStatsEx[]>(() => {
    return [...(this.strategyStats()?.data ?? []), ...(this.strategyTargetHistory() ?? [])];
  });
  readonly chartConfig = computed<ChartConfig<AdStatsEx, "line">>(() => ({
    type: "line",
    aspectRatio: 3,
    aggFunctions: [mergeAdStatsWithTargetHistory],
    metrics: this.selectedMetrics(),
    dateAggregation: DateAggregation.daily,
    dateRange: this.selectedDateRange(),
    previousPeriodData: this.strategyStats()?.previousPeriodData,
    previousPeriod: this.periodComparison()?.period,
    eventAnnotations: this.strategyEventAnnotation(),
    metricsOnSameScale: [
      [AD_SALES, COST, TOTAL_SALES],
      [TACOS, TARGET_TACOS],
    ],
  }));

  gridOptions = signal<GridOptions>({
    columnDefs: [
      {
        headerName: this.translocoService.translate("tacos-strategy.ad_type", {}, "en"),
        field: "campaignType",
        cellRenderer: CampaignTypeBadgeGridComponent,
        cellRendererParams: (params: ICellRendererParams<AdStatsEx>) => ({
          campaignType: params.value,
        }),
      },
      ...this.gridConfigService
        .getMetricsColDef<AdStatsEx>(this.METRICS.filter((m) => m.category !== MetricCategory.SALES_STATS))
        .map(({ headerName, ...def }) => ({
          ...def,
          cellRendererParams: (params: ICellRendererParams<AdStatsEx>) => {
            return {
              ...(def as ColDef<AdStatsEx>).cellRendererParams(params),
              currency: this.currency(),
              locale: this.locale(),
              previousData:
                this.periodComparison()?.period && params.data
                  ? this.previousPeriodRowStats().get(params.data.campaignType)
                  : undefined,
            };
          },
        })),
      {
        ...actionColumnProperties<AdStatsEx, string>(),
        cellRendererSelector: (params: ICellRendererParams<AdStatsEx>) => {
          if (params.node.data?.campaignType !== StatsCampaignType.SB) return undefined;
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: [
                {
                  icon: "list",
                  tooltip: this.translocoService.translate("dsp-strategy-stats-grid.display_creative_stats_details"),
                  onClick: (params: ICellRendererParams<AdStatsEx>) => {
                    this.openCreativeStatsDetails(params);
                  },
                },
              ] as ActionButton[],
            },
          };
        },
      },
    ],
  });

  public selectMetric(metrics: Metric<StrategyTargetHistory>[]) {
    if (metrics.includes(TACOS)) {
      metrics = metrics.includes(TARGET_TACOS) ? metrics : [...metrics, TARGET_TACOS];
    } else {
      metrics = metrics.filter((m) => m !== TARGET_TACOS);
    }
    this.selectedMetrics.set([...metrics]);
  }

  private getStatsByCampaignType(allStats: AdStatsEx[]): Map<StatsCampaignType | undefined, AdStatsEx> {
    const grouped: Map<StatsCampaignType | undefined, AdStatsEx> = adStatsGroupBy(
      allStats,
      (s: AdStatsEx) => s.campaignType,
    );
    grouped.delete(undefined);
    return grouped;
  }

  private openCreativeStatsDetails(params: ICellRendererParams<AdStatsEx>) {
    if (!this.creativeStatsDetailsModal()) return;
    this.modalService.openModal<unknown, void>(this.creativeStatsDetailsModal()!, {
      modalTitle: "Creative stats details",
    });
  }
}
